import { API_URL } from '@/config/apiConfig';
import { whenUserAuthReady } from '@/utils/authReady';
/**
 * Authenticated Fetch Utility (Iter-316 cookie-only — Tier 2 cleanup)
 * ===================================================================
 * Wraps native fetch with Corvana auth plumbing:
 *   1. Always sends `credentials: 'include'` so the httpOnly access_token
 *      cookie travels cross-origin (CORS is configured with explicit origins
 *      + allow_credentials=true on the backend).
 *   2. Reads the non-httpOnly `csrf_token` cookie and mirrors it as the
 *      `X-CSRF-Token` header on state-changing requests (double-submit).
 *
 * Iter-316 removed the legacy `Authorization: Bearer` header derived from
 * browser-local storage (a `corvana_access_token` key read via the
 * browser-storage API). That key is no longer written by
 * AuthContext and reading it would mask the cookie regression surface
 * we want to detect.
 */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Notify the app that the backend briefly can't reach its database (HTTP 503).
// A top-level banner listens for this and prompts the user to retry, so a
// transient MongoDB failover degrades gracefully instead of throwing raw
// errors. Throttled so a burst of failing calls raises at most one banner.
let _lastServiceUnavailableAt = 0;
function notifyServiceUnavailable() {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - _lastServiceUnavailableAt < 4000) return;
  _lastServiceUnavailableAt = now;
  try { window.dispatchEvent(new CustomEvent('corvana:service-unavailable')); } catch { /* noop */ }
}

function getCsrfToken() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function getAuthHeaders() {
  // Cookie-only auth — no Authorization header needed.
  return {};
}

export async function authFetch(url, options = {}) {
  // Wait for the auth provider to finish establishing the session before
  // firing — prevents the first-paint flood of 401 / "Failed to fetch"
  // from requests that race ahead of the cookie handshake. Callers in
  // a genuine pre-auth flow should use `safeFetch` instead. Bypassable
  // via `options.skipAuthGate` for the rare ordering-sensitive caller.
  // `skipAuthInterceptor` is accepted as an alias so the SAME opt-out flag
  // works across both authFetch (CRM) and adminFetch (Tech Panel) — passing
  // the Tech-Panel name here used to be silently ignored (naming-collision
  // footgun flagged by the Watchtower audit). This only skips the client-side
  // readiness gate; it never affects CSRF or the 401 interceptor.
  if (!options.skipAuthGate && !options.skipAuthInterceptor) {
    await whenUserAuthReady();
  }

  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  // Attach CSRF header on mutating requests when we have a csrf cookie.
  if (MUTATING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf && !headers['X-CSRF-Token']) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  // Iter-278-pt5 — route through the preserved native fetch captured
  // in index.html BEFORE emergent-main.js wrapped window.fetch. The
  // platform wrapper races on response.clone() for telemetry and
  // fails with "Response body is already used" when our code consumes
  // the body first; bypassing the wrapper eliminates the class of bug
  // entirely. Falls back to window.fetch in SSR / unit-test contexts
  // where the bootstrap script never ran.
  const baseFetch =
    (typeof window !== 'undefined' && window.__corvanaNativeFetch) ||
    fetch;

  try {
    const res = await baseFetch(url, {
      credentials: 'include',   // always send cookies — harmless for legacy Bearer callers
      ...options,
      headers,
    });
    if (res && res.status === 503) notifyServiceUnavailable();
    // Proactive support: surface server failures (5xx) to the telemetry layer
    // without consuming the body. Expected 4xx (auth/validation) are skipped.
    // 503 is EXCLUDED — it's a transient DB-reconnect that already shows its own
    // calm "reconnecting… retry" banner; firing the "team alerted" toast on top
    // of it double-notifies and needlessly alarms the operator.
    if (res && res.status >= 500 && res.status !== 503 && !options.skipTelemetry && typeof window !== 'undefined' && window.__corvanaTelemetry) {
      try { window.__corvanaTelemetry.reportApiError(res.status, url); } catch { /* noop */ }
    }
    return res;
  } catch (err) {
    // Skip telemetry for failures that are NOT genuine server/route problems:
    //  • intentionally-aborted requests (AbortController / component unmount)
    //  • requests the browser CANCELS during page navigation/reload/close —
    //    these throw `TypeError: Failed to fetch` (name !== 'AbortError') and
    //    were previously misreported as "broken routes" in the weekly digest.
    //  • the client being offline (their connectivity, not our server).
    const aborted = err?.name === 'AbortError' || options?.signal?.aborted;
    const navigating = typeof window !== 'undefined' && window.__corvanaUnloading === true;
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (!aborted && !navigating && !hidden && !offline && !options.skipTelemetry &&
        typeof window !== 'undefined' && window.__corvanaTelemetry) {
      try { window.__corvanaTelemetry.reportNetworkError(url); } catch { /* noop */ }
    }
    throw err;
  }
}

export async function authGet(path) {
  return authFetch(`${API_URL}${path}`);
}

// Like authGet, but never fires the "team's been alerted" telemetry toast.
// Use for non-critical background/optional panels where a slow or failing
// call should degrade silently rather than alarm the operator.
export async function authGetQuiet(path) {
  return authFetch(`${API_URL}${path}`, { skipTelemetry: true });
}

export async function authPost(path, body) {
  return authFetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// authPost that never fires the error toast — for background prefetch/warming.
export async function authPostQuiet(path, body) {
  return authFetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    skipTelemetry: true,
  });
}

export async function authPut(path, body) {
  return authFetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function authDelete(path) {
  return authFetch(`${API_URL}${path}`, { method: 'DELETE' });
}

export async function authPatch(path, body) {
  return authFetch(`${API_URL}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * safeFetch — drop-in replacement for native `fetch()` that bypasses the
 * platform's `emergent-main.js` window.fetch wrapper. The wrapper races
 * on `response.clone()` for telemetry and fails with
 * "Failed to execute 'clone' on 'Response': Response body is already used"
 * whenever our code consumes the body first (which is most of the time).
 *
 * Use this in PUBLIC / unauthenticated flows (signup, registration,
 * marketing pages) where authFetch's auth-header injection isn't wanted.
 * Identical signature to native `fetch()` so callsites need no changes
 * beyond the function name.
 */
export function safeFetch(url, options = {}) {
  const baseFetch =
    (typeof window !== 'undefined' && window.__corvanaNativeFetch) ||
    fetch;
  return baseFetch(url, options).then((res) => {
    if (res && res.status === 503) notifyServiceUnavailable();
    return res;
  });
}

export { API_URL, getCsrfToken };
